import { ThemeEvents } from '@theme/events';

/**
 * CUSTOM: A custom element that displays the selected variant option (by default
 * the colour) as its own line under the product title.
 *
 * It mirrors how `product-price.js` works: when a variant changes, the theme
 * fetches fresh section HTML and dispatches a `variantUpdate` event carrying it.
 * This element finds its own counterpart in that HTML and swaps in the new value,
 * so the colour name stays in sync on the product page, in product cards, in
 * quick-add dialogs, and anywhere else the block is used.
 */
class ProductVariantTitle extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog, product-card');
    if (!closestSection) return;

    this.#target = closestSection;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updateVariantTitle);
  }

  disconnectedCallback() {
    this.#target?.removeEventListener(ThemeEvents.variantUpdate, this.updateVariantTitle);
    this.#target = null;
  }

  /** @type {Element | null} */
  #target = null;

  /**
   * Replaces the rendered value with the one from the updated section HTML.
   * @param {CustomEvent} event - The variant update event.
   */
  updateVariantTitle = (event) => {
    const data = event.detail?.data;
    if (!data?.html) return;

    // When a card switches to a different product (combined listings), adopt its id.
    if (data.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId &&
      this.dataset.productId &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const selector = this.dataset.blockId
      ? `product-variant-title[data-block-id="${this.dataset.blockId}"]`
      : 'product-variant-title';

    const incoming = data.html.querySelector(selector) ?? data.html.querySelector('product-variant-title');
    if (!incoming) return;

    // Only the text is copied across, never the markup. The card re-render
    // endpoint emits a minimal version of this element without the block's
    // typography classes, so replacing nodes would drop the styling.
    const newText = incoming.querySelector('[data-variant-title-value]')?.textContent?.trim();
    if (!newText) return;

    const target = this.#valueElement();
    if (!target) return;

    target.textContent = newText;
    this.commitPreview();
  };

  /**
   * The element holding the rendered value.
   * @returns {Element | null}
   */
  #valueElement() {
    return this.querySelector('[data-variant-title-value] p') ?? this.querySelector('[data-variant-title-value]');
  }

  /**
   * Temporarily shows a different value, used while hovering a swatch on a
   * product card so the colour name previews alongside the image.
   * @param {string} value - The value to show.
   */
  preview(value) {
    if (typeof value !== 'string' || value === '') return;

    const target = this.#valueElement();
    if (!target) return;

    if (this.#committedValue === null) {
      this.#committedValue = target.textContent;
    }
    target.textContent = value;
  }

  /**
   * Restores the value that was showing before {@link preview} was called.
   */
  resetPreview() {
    if (this.#committedValue === null) return;

    const target = this.#valueElement();
    if (target) target.textContent = this.#committedValue;
    this.#committedValue = null;
  }

  /**
   * Forgets any stored preview value, so the currently rendered value becomes
   * the one a later reset returns to.
   */
  commitPreview() {
    this.#committedValue = null;
  }

  /** @type {string | null} */
  #committedValue = null;
}

if (!customElements.get('product-variant-title')) {
  customElements.define('product-variant-title', ProductVariantTitle);
}
